// ContractExpedienteLink — Widget vinculación contrato↔expediente
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DocumentTextIcon, LinkIcon } from '@heroicons/react/24/outline';
import { contractsService } from '@/services/contractsService';
import { toast } from 'sonner';

interface ContractExpedienteLinkProps {
  hiringCode: string;
  expedienteId?: string | null;
  onLinked?: () => void;
}

export function ContractExpedienteLink({
  hiringCode,
  expedienteId,
  onLinked,
}: ContractExpedienteLinkProps) {
  const [showForm, setShowForm] = useState(false);
  const [inputId, setInputId] = useState('');
  const [linking, setLinking] = useState(false);

  const handleLink = async () => {
    if (!inputId.trim()) return;
    setLinking(true);
    try {
      await contractsService.linkExpediente(hiringCode, inputId.trim());
      toast.success('Expediente vinculado correctamente');
      setShowForm(false);
      setInputId('');
      onLinked?.();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      toast.error(msg || 'Error al vincular expediente');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <DocumentTextIcon className="w-5 h-5" />
          Expediente
        </CardTitle>
      </CardHeader>
      <CardContent>
        {expedienteId ? (
          <div className="flex items-center justify-between">
            <Link
              to={`/crm/expedientes/${expedienteId}`}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            >
              <LinkIcon className="w-4 h-4" />
              Ver expediente
            </Link>
            <Badge variant="outline" className="text-xs">
              Vinculado
            </Badge>
          </div>
        ) : (
          <>
            {!showForm ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowForm(true)}
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Vincular expediente
              </Button>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="ID del expediente (UUID)"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value)}
                  disabled={linking}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleLink}
                    disabled={linking || !inputId.trim()}
                  >
                    {linking ? 'Vinculando...' : 'Vincular'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowForm(false);
                      setInputId('');
                    }}
                    disabled={linking}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
